import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInWeeks
} from 'date-fns'

/**
 * Feature switch for the whole notification centre — the bell in all three
 * toolbars and the /notifications page. It ships dark: until the backend
 * exists the UI would be presenting invented data as if it were the user's, so
 * the centre is opt-in per environment (`REACT_APP_NOTIFICATIONS_ENABLED=true`
 * in the dev/preview `.env`), the same build-time switch Gleap and the
 * Accelerate dev tools already use.
 */
export const NOTIFICATIONS_ENABLED =
  process.env.REACT_APP_NOTIFICATIONS_ENABLED === 'true'

/**
 * Flip to `true` the moment the `notificationsGet` / `notificationsMarkRead` /
 * `notificationsMarkAllRead` / `notificationEvent` events exist on the bus.
 * Everything either side of this flag is already the real path — the slice, the
 * selectors, the event-bus definitions and the components all stay exactly as
 * they are; only `mock-notifications.js` goes away.
 *
 * See uxspecs/notifications/notification-center-requirements.md §6 (open
 * question 12: backend contract, push vs poll).
 */
export const NOTIFICATIONS_API_ENABLED = false

/**
 * How many notifications a page request asks for (NC-31). The panel only ever
 * sees the first page; the full page appends the rest on demand.
 */
export const NOTIFICATIONS_PAGE_SIZE = 10

/**
 * The panel is a glance, not the archive: it renders at most this many of the
 * loaded items and defers the rest to "View all notifications". Without the cap
 * it would grow every time the full page appends another page into the shared
 * slice.
 */
export const PANEL_MAX_ITEMS = 6

/**
 * Notification kinds. Lives here rather than alongside the mock data so
 * production code never imports from a file named "mock" — the API will send
 * these same values.
 */
export const NOTIFICATION_TYPE = {
  CONTENT: 'content',
  FEATURE: 'feature',
  DIGEST: 'digest',
  MENTION: 'mention',
  ACHIEVEMENT: 'achievement'
}

/**
 * What clicking a notification does, beyond marking it read.
 *
 * - NAVIGATE: the destination is a page — go straight there, no intermediate
 *   modal (playlist, skill bite, quiz).
 * - MINDSET: open the M:Coach agent in place via `useMindset` — the agent is
 *   itself an overlay, so an interstitial modal would be a modal-before-a-modal.
 * - MODAL: there is no destination page (welcome tour, what's-new changelog,
 *   org recommendations) — the panel opens a purpose-built modal instead.
 */
export const NOTIFICATION_ACTION = {
  NAVIGATE: 'navigate',
  MINDSET: 'mindset',
  MODAL: 'modal'
}

/**
 * The types a user can switch off in Account Settings (NC-40), in the order
 * they are listed there. `mention` is deliberately absent: comments are not
 * confirmed for v1 (§6 open question 7), and offering a switch for something
 * that never arrives is worse than offering nothing.
 */
export const NOTIFICATION_PREFERENCE_TYPES = [
  {
    key: NOTIFICATION_TYPE.CONTENT,
    label: 'New content for you',
    description: 'Skill Bites, playlists and articles in the topics you follow.'
  },
  {
    key: NOTIFICATION_TYPE.FEATURE,
    label: 'Product news and tips',
    description: 'New features, guided tours and what\'s new in Mindtools.'
  },
  {
    key: NOTIFICATION_TYPE.DIGEST,
    label: 'Weekly digest',
    description: 'A summary of your progress and what is waiting for you.'
  },
  {
    key: NOTIFICATION_TYPE.ACHIEVEMENT,
    label: 'Achievements',
    description: 'Badges and milestones as you complete your learning.'
  }
]

/** Everything on until the user says otherwise. */
export const DEFAULT_NOTIFICATION_PREFERENCES = NOTIFICATION_PREFERENCE_TYPES.reduce(
  (defaults, { key }) => ({ ...defaults, [key]: true }),
  {}
)

export const NOTIFICATION_MODAL = {
  WELCOME_TOUR: 'welcomeTour',
  WHATS_NEW: 'whatsNew',
  ORG_RECOMMENDS: 'orgRecommends'
}

/**
 * Error and empty copy live here because the panel and the full page render
 * the same words — one edit, both surfaces.
 */
export const NOTIFICATION_ERROR_COPY = {
  title: "Couldn't load notifications.",
  body: 'Check your connection and try again.',
  retry: 'Try again'
}

export const NOTIFICATION_EMPTY_COPY = {
  all: {
    title: "You're all caught up.",
    body: 'New notifications will show up here.'
  },
  unread: {
    title: 'No unread notifications.',
    body: 'Anything new will appear here.'
  }
}

/**
 * Compact relative time ("2h", "3d") as drawn in the wireframe.
 *
 * `date-fns` `formatDistance` — used by the comments component — renders
 * "about 2 hours", which is far too long for the panel's meta column, so the
 * compacting is done here off the same library's diff helpers.
 */
export const formatTimeAgo = (isoDate, now = new Date()) => {
  if (!isoDate) return ''
  const date = new Date(isoDate)
  if (isNaN(date.getTime())) return ''

  const minutes = differenceInMinutes(now, date)
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`

  const hours = differenceInHours(now, date)
  if (hours < 24) return `${hours}h`

  const days = differenceInDays(now, date)
  if (days < 7) return `${days}d`

  return `${differenceInWeeks(now, date)}w`
}
